import React, { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';

import { SectionHeader } from '@/components/common';
import * as videoApi from '@/apis/videos';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { useUploadMidibusMedia, useDeleteMidibusMedia } from '@/hooks/videos.hook';

/**
 * 동영상 등록 모달 컴포넌트
 * 
 * midibus 연계를 통한 동영상 업로드 및 메타데이터 저장 모달입니다.
 * 
 * 입력 필드:
 * - course_id: 과정 ID (props로 받음)
 * - course_category: 과정 카테고리
 * - video_seq: 동영상 순서 (폼 입력)
 * - video_name: 동영상명 (폼 입력)
 * - media_id: 미디버스 업로드 후 받아옴
 * - video_intro: 동영상 소개 (폼 입력)
 * - video_object: 동영상 목표 (폼 입력)
 * - attached_link: 첨부 링크 (폼 입력)
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {number} props.courseId - 과정 ID
 * @param {Object} props.courseData - 과정 상세 정보
 * @param {Function} props.onSuccess - 업로드 성공 후 콜백
 */
const VideoAddModal = ({
  open,
  onClose,
  courseId,
  courseCategory,
  onSuccess,
}) => {
  // 파일 선택 상태
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [mediaId, setMediaId] = useState(null); // 미디버스 업로드 후 받은 media_id
  const [fileDuration, setFileDuration] = useState(null);

  const uploadMidibusMutation = useUploadMidibusMedia();
  const deleteMidibusMutation = useDeleteMidibusMedia();

  // 폼 관리
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      videoSeq: 1,
      videoName: '',
      videoIntro: '',
      videoObject: '',
      attachedLink: '',
      mediaId: null,
    },
  });

  // 모달이 열릴 때 폼 초기화
  useEffect(() => {
    if (open) {
      reset({
        videoSeq: 1,
        videoName: '',
        videoIntro: '',
        videoObject: '',
        attachedLink: '',
        mediaId: null,
      });
      setSelectedFile(null);
      setMediaId(null);
      setUploadProgress(false);
      setValue('mediaId', null);
    }
  }, [open, reset, setValue]);

  /**
   * 파일 선택 핸들러
   */
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      // 동영상 파일 확장자 검증
      const validExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        errorToast('동영상 파일만 업로드 가능합니다. (mp4, avi, mov, wmv, flv, webm, mkv)');
        return;
      }

      // 파일 크기 검증 (예: 2GB 제한)
      const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
      if (file.size > maxSize) {
        errorToast('파일 크기는 2GB를 초과할 수 없습니다.');
        return;
      }

      setSelectedFile(file);
      setMediaId(null); // 새 파일 선택 시 media_id 초기화
      setValue('mediaId', null); // 폼 필드도 초기화
    }
  }, []);

  const formatDuration = useCallback((duration) => {
    if (!Number.isFinite(duration)) {
      return '알 수 없음';
    }

    const totalSeconds = Math.floor(duration);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}시간 ${minutes}분 ${seconds}초`;
    }

    return `${minutes}분 ${seconds}초`;
  }, []);

  const getVideoDuration = useCallback((file) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      cleanup();
      resolve(video.duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('동영상 길이를 확인할 수 없습니다.'));
    };
    video.src = objectUrl;
  }), []);

  useEffect(() => {
    if (!selectedFile) {
      setFileDuration(null);
      return;
    }

    let isCancelled = false;

    getVideoDuration(selectedFile)
      .then((duration) => {
        if (!isCancelled) {
          setFileDuration(duration);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setFileDuration(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedFile, getVideoDuration]);

  const handleMidibusUpload = useCallback(async () => {
    if (!selectedFile) {
      errorToast('동영상 파일을 선택해주세요.');
      return;
    }

    let duration = fileDuration;
    if (!Number.isFinite(duration)) {
      try {
        duration = await getVideoDuration(selectedFile);
        setFileDuration(duration);
      } catch (error) {
        duration = null;
      }
    }

    const lastModifiedText = selectedFile.lastModified
      ? new Date(selectedFile.lastModified).toLocaleString('ko-KR')
      : '알 수 없음';
    const durationText = formatDuration(duration);
    const confirmText = `업로드하시겠습니까?\n파일명: ${selectedFile.name}\n마지막 수정시간: ${lastModifiedText}\n동영상 길이: ${durationText}`;

    confirmToast(confirmText, async () => {
      try {
        setUploadProgress(true);
        const videoName = watch('videoName') || selectedFile.name;
        const uploadedMediaId = await uploadMidibusMutation.mutateAsync({
          file: selectedFile,
          videoName,
          courseId,
        });
        setMediaId(uploadedMediaId);
        setValue('mediaId', uploadedMediaId); // 폼 필드에도 값 설정
        successToast('미디버스 업로드가 완료되었습니다.');
      } catch (error) {
        const errorMessage = error.message || '미디버스 업로드에 실패했습니다.';
        errorToast(errorMessage);
        console.error('미디버스 업로드 오류:', error);
      } finally {
        setUploadProgress(false);
      }
    });
  }, [selectedFile, fileDuration, getVideoDuration, formatDuration, watch]);

  /**
   * 동영상 등록 제출 핸들러
   */
  const onSubmit = useCallback(
    async (data) => {
      if (!courseCategory) {
        errorToast('과정 정보를 불러올 수 없습니다.');
        return;
      }
      setUploadProgress(true);
      try {
        const videoData = {
          courseId: parseInt(courseId),
          courseCategory: courseCategory,
          videoSeq: parseInt(data.videoSeq) || 1,
          videoName: data.videoName,
          mediaId: mediaId,
          videoIntro: data.videoIntro || '',
          videoObject: data.videoObject || '',
          attachedLink: data.attachedLink || '',
        };

        await videoApi.createVideo(videoData);

        successToast('동영상이 성공적으로 등록되었습니다.');
        setSelectedFile(null);
        setMediaId(null);
        setValue('mediaId', null);
        onSuccess?.();
        onClose();
      } catch (error) {
        const errorMessage = error.message || '동영상 등록에 실패했습니다.';
        errorToast(errorMessage);
        console.error('동영상 등록 오류:', error);
      } finally {
        setUploadProgress(false);
      }
    },
    [courseCategory, courseId, mediaId, onSuccess, onClose]
  );

  /**
   * 모달 닫기 핸들러
   */
  const handleClose = useCallback(async () => {
    if (!uploadProgress) {
      // 미디버스에 업로드된 동영상이 있으면 삭제 후 모달 닫기
      if (mediaId) {
        try {
          await deleteMidibusMutation.mutateAsync({
            courseId: courseId,
            mediaId: mediaId,
          });
        } catch (error) {
          errorToast('미디버스 동영상 삭제에 실패했습니다. 수동으로 삭제해주세요.');
        }
      }
      setSelectedFile(null);
      setMediaId(null);
      setValue('mediaId', null);
      onClose();
    }
  }, [uploadProgress, onClose, setValue, deleteMidibusMutation, courseId, mediaId]);

  if (!courseCategory) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        동영상 등록
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)} id="video-add-form">
          <Stack spacing={3}>
            {/* 과정 정보 */}
            <Box>
              {!mediaId && selectedFile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  미디버스에 동영상을 업로드한 후 등록해주세요.
                </Alert>
              )}
              <SectionHeader sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  과정 정보
                </Typography>
              </SectionHeader>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    과정 ID
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {courseId}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    과정 카테고리
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {courseCategory || '-'}
                  </Typography>
                </Box>
              </Stack>
              {mediaId && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  미디버스 Media ID: {mediaId}
                </Alert>
              )}
            </Box>

            {/* 동영상 정보 입력 */}
            <Box>
              <SectionHeader sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  동영상 정보
                </Typography>
              </SectionHeader>
              <Stack spacing={2}>
                {/* 동영상 파일 선택 */}
                <Box>
                  <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                    동영상 파일 *
                  </Typography>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    disabled={uploadProgress}
                    style={{ display: 'none' }}
                    id="video-file-input"
                  />
                  <label htmlFor="video-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      disabled={uploadProgress}
                      sx={{ mb: 1 }}
                    >
                      {selectedFile ? selectedFile.name : '동영상 파일 선택'}
                    </Button>
                  </label>
                  {selectedFile && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Alert severity="success">
                        선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Alert>
                      <Button
                        variant="contained"
                        startIcon={uploadProgress ? <CircularProgress size={18} /> : <UploadIcon />}
                        onClick={handleMidibusUpload}
                        disabled={uploadProgress || !selectedFile || !!mediaId}
                      >
                        {mediaId ? '업로드 완료' : '미디버스에 업로드하기'}
                      </Button>
                    </Stack>
                  )}
                </Box>

                {/* 동영상명 */}
                <Controller
                  name="videoName"
                  control={control}
                  rules={{
                    required: '동영상명을 입력해주세요.',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상명 *"
                      placeholder="[01] 시큐어코딩 개요"
                      error={!!errors.videoName}
                      helperText={errors.videoName?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 동영상 순서 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="videoSeq"
                    control={control}
                    rules={{
                      required: '동영상 순서를 입력해주세요.',
                      min: { value: 1, message: '순서는 1 이상이어야 합니다.' },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="동영상 순서 *"
                        placeholder="1"
                        error={!!errors.videoSeq}
                        helperText={errors.videoSeq?.message}
                        disabled={uploadProgress}
                        sx={{ width: '200px' }}
                      />
                    )}
                  />
                </Box>

                {/* 동영상 소개 */}
                <Controller
                  name="videoIntro"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상 소개"
                      placeholder="동영상에 대한 간단한 소개를 입력하세요"
                      multiline
                      rows={3}
                      error={!!errors.videoIntro}
                      helperText={errors.videoIntro?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 동영상 목표 */}
                <Controller
                  name="videoObject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상 목표"
                      placeholder="이 동영상을 통해 학습할 목표를 입력하세요"
                      multiline
                      rows={3}
                      error={!!errors.videoObject}
                      helperText={errors.videoObject?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 첨부 링크 */}
                <Controller
                  name="attachedLink"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="첨부 링크"
                      placeholder="관련 자료 링크를 입력하세요 (예: https://example.com)"
                      error={!!errors.attachedLink}
                      helperText={errors.attachedLink?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* mediaId (수정 불가_업로드 시 자동 입력) */}
                <Controller
                  name="mediaId"
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="hidden" />
                  )}
                />
              </Stack>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadProgress}>
          취소
        </Button>
        <Button
          type="submit"
          form="video-add-form"
          variant="contained"
          startIcon={uploadProgress ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={uploadProgress || !selectedFile || !mediaId}
        >
          {uploadProgress ? '업로드 중...' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoAddModal;
